import { Extent } from 'openlayers'
import { LayerActiveView } from 'src/components/layerpicker/LayerPicker'
import { BaseAction, DataAction } from 'src/CustomTypes'
import {
  initialLayerParams,
  LayerCapabilities,
  LayerGroup,
  LayerParams,
  LayerParamsObject,
  LayerType
} from 'src/dataSources/layers/LayerRegistry'
import { LayerViewType } from 'src/Enums'
import {
  ADD_LAYER,
  REMOVE_LAYER,
  CLEAR_LAYERS,
  SET_LAYER_CAPABILITIES,
  SET_LAYER_PARAMS,
  ADD_LAYER_GROUP,
  REMOVE_LAYER_GROUP,
  SET_LAYER_ACTIVE_VIEW,
  SET_MAP_CENTER,
  SET_MAP_CURRENT_ZOOM,
  SET_COLORS_IN_FOCUS,
  ColorInFocus,
  SET_MAP_EXTENT
} from '../actions/MapActions'

export interface MapState {
  showOrtoImages: boolean
  mapCenter: ol.Coordinate
  mapCurrentZoom: number
  mapExtent: Extent
  colorsInFocus: ColorInFocus[]
  enabledLayerGroups: LayerGroup[]
  enabledLayers: LayerType[]
  layerParams: LayerParams[]
  modifiedLayer?: LayerType
  layerCapabilities: LayerCapabilities[]
  layerActiveViews: LayerActiveView[]
}

export const mapStateInitial: MapState = {
  showOrtoImages: false,
  mapCenter: [550232.2914831298, 6178702.8859613435],
  mapCurrentZoom: 8.1,
  mapExtent: [0, 0, 0, 0],
  colorsInFocus: [],
  enabledLayerGroups: [LayerGroup.SUBSURFACE],
  enabledLayers: [LayerType.OSM_BACKGROUND, LayerType.MODEL_AREAS],
  layerParams: initialLayerParams,
  layerCapabilities: [],
  layerActiveViews: []
}

export function mapReducer (
  state: MapState = mapStateInitial, // eslint-disable-line @typescript-eslint/default-param-last
  action: BaseAction): MapState {
  switch (action.type) {
    case SET_MAP_CENTER: {
      const a = action as DataAction<ol.Coordinate>
      return {
        ...state,
        mapCenter: a.data
      }
    }
    case SET_MAP_CURRENT_ZOOM: {
      const a = action as DataAction<number>
      return {
        ...state,
        mapCurrentZoom: a.data
      }
    }
    case SET_MAP_EXTENT: {
      const a = action as DataAction<Extent>
      return {
        ...state,
        mapExtent: a.data
      }
    }
    case SET_COLORS_IN_FOCUS: {
      const a = action as DataAction<ColorInFocus[]>
      return {
        ...state,
        colorsInFocus: a.data
      }
    }
    case ADD_LAYER: {
      const a = action as DataAction<LayerType>
      let layerTypes = [...state.enabledLayers]
      const layerAlreadyEnabled = state.enabledLayers.some(layerType => layerType === a.data)
      if (!layerAlreadyEnabled) {
        layerTypes = [...state.enabledLayers, a.data]
      }
      return {
        ...state,
        enabledLayers: layerTypes,
        layerActiveViews: [...state.layerActiveViews, { type: a.data, view: LayerViewType.SETTINGS }]
      }
    }
    case REMOVE_LAYER: {
      const a = action as DataAction<LayerType>
      return {
        ...state,
        enabledLayers: state.enabledLayers.filter(layerType => layerType !== a.data),
        layerActiveViews: state.layerActiveViews.filter(layerView => layerView.type !== a.data)
      }
    }
    case ADD_LAYER_GROUP: {
      const a = action as DataAction<LayerGroup>
      let layerGroups = [...state.enabledLayerGroups]
      const layerGroupAlreadyEnabled = state.enabledLayerGroups.some(layerGroup => layerGroup === a.data)
      if (!layerGroupAlreadyEnabled) {
        layerGroups = [...state.enabledLayerGroups, a.data]
      }
      return {
        ...state,
        enabledLayerGroups: layerGroups
      }
    }
    case REMOVE_LAYER_GROUP: {
      const a = action as DataAction<LayerGroup>
      return {
        ...state,
        enabledLayerGroups: state.enabledLayerGroups.filter(layerGroup => layerGroup !== a.data)
      }
    }
    case CLEAR_LAYERS:
      return {
        ...state,
        enabledLayers: mapStateInitial.enabledLayers,
        enabledLayerGroups: mapStateInitial.enabledLayerGroups,
        layerActiveViews: []
      }
    case SET_LAYER_ACTIVE_VIEW: {
      const a = action as DataAction<LayerActiveView>
      const layerActiveViews = state.layerActiveViews.filter(layerView => layerView.type !== a.data.type)
      return {
        ...state,
        layerActiveViews: [...layerActiveViews, a.data]
      }
    }
    case SET_LAYER_PARAMS: {
      const a = action as DataAction<LayerParams>
      let params = state.layerParams.map(param => {
        if (param.layerType === a.data.layerType) {
          return a.data
        } else {
          return param
        }
      })
      if (!params.some(param => param.layerType === a.data.layerType)) {
        params = [...params, a.data]
      }
      return {
        ...state,
        layerParams: params,
        modifiedLayer: a.data.layerType
      }
    }
    case SET_LAYER_CAPABILITIES: {
      const a = action as DataAction<LayerCapabilities>
      let capabilities = state.layerCapabilities.map(capability => {
        if (capability.type === a.data.type) {
          return a.data
        } else {
          return capability
        }
      })
      if (!capabilities.some(capability => capability.type === a.data.type)) {
        capabilities = [...capabilities, a.data]
      }
      return {
        ...state,
        layerCapabilities: capabilities
      }
    }
    default:
      return state
  }
}

export const getLayerParamsFromType = (type: LayerType, params: LayerParams[]): LayerParamsObject | undefined => {
  return params.find(param => param.layerType === type)?.params
}

export const getLayerCapabilitiesFromType = (type: LayerType, capabilities: LayerCapabilities[]): ol.GlobalObject | undefined => {
  return capabilities.find(capability => capability.type === type)?.capabilities
}
